'use client'

import React, { useState } from 'react'
import type { FAQBlock } from '@/payload-types'
import { cn } from '@/utilities/cn'

type Props = {
  className?: string
} & FAQBlock

export const FAQBlockComponent: React.FC<Props> = (props) => {
  const { 
    heading,
    description,
    faqs,
    className 
  } = props
  
  const [openItems, setOpenItems] = useState<number[]>([])
  
  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    )
  }
  
  return (
    <section className={cn("py-20 lg:py-32 bg-gray-50", className)}>
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            {heading && (
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#211F54] mb-6">
                {heading}
              </h2>
            )}
            {description && (
              <p className="text-gray-600 text-lg leading-relaxed">
                {description}
              </p>
            )}
          </div>
          
          {/* FAQ Items */}
          {faqs && faqs.length > 0 && (
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg overflow-hidden shadow-sm"
                >
                  <button
                    onClick={() => toggleItem(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
                    aria-expanded={openItems.includes(index)}
                  >
                    <h3 className="text-lg font-semibold text-[#211F54] pr-4">
                      {faq.question}
                    </h3>
                    <div className="flex-shrink-0">
                      <svg
                        className={cn(
                          "w-5 h-5 text-gray-600 transition-transform duration-200",
                          openItems.includes(index) && "rotate-180"
                        )}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </button>
                  
                  <div
                    className={cn(
                      "grid transition-all duration-200 ease-in-out",
                      openItems.includes(index) 
                        ? "grid-rows-[1fr] opacity-100" 
                        : "grid-rows-[0fr] opacity-0"
                    )}
                  >
                    <div className="overflow-hidden">
                      <div className="px-6 pb-5 pt-0">
                        <p className="text-gray-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}